import {
  Button,
  Chip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  useDisclosure,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { Plus, Tag } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api-client";
import { GetCustomerNoteTags } from "@/server-types";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { ManageCustomerNoteTagModal } from "@/Components/NextBase/Customer/ManageCustomerNoteTagModal";

type AvailableDefault =
  GetCustomerNoteTags["response"]["availableDefaults"][number];
type AvailableTag = GetCustomerNoteTags["response"]["tags"][number];

export const CustomerNoteLabelPicker = ({
  selectedTagIds,
  selectedDefaultIds,
  onChangeTagIds,
  onChangeDefaultIds,
  isDisabled,
}: {
  selectedTagIds: number[];
  selectedDefaultIds: string[];
  onChangeTagIds: (ids: number[]) => void;
  onChangeDefaultIds: (ids: string[]) => void;
  isDisabled?: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const {
    isOpen: isCreateTagModalOpen,
    onOpen: openCreateTagModal,
    onClose: closeCreateTagModal,
  } = useDisclosure();

  const {
    data: tagsData,
    isFetching: isFetchingTags,
    processedErrorMessage: processedErrorMessageTags,
  } = api.customerNote.useGetCustomerNoteTags({
    disabled: false,
  });

  const appliedDefaults = (tagsData?.availableDefaults ?? []).filter((d) =>
    selectedDefaultIds.includes(d.id),
  );
  const appliedTags = (tagsData?.tags ?? []).filter((tag) =>
    selectedTagIds.includes(tag.id),
  );
  const suggestedDefaults = (tagsData?.availableDefaults ?? []).filter(
    (d) => !selectedDefaultIds.includes(d.id),
  );
  const suggestedTags = (tagsData?.tags ?? []).filter(
    (tag) => !selectedTagIds.includes(tag.id),
  );

  const selectDefault = (def: AvailableDefault) => {
    if (selectedDefaultIds.includes(def.id)) return;
    onChangeDefaultIds([...selectedDefaultIds, def.id]);
  };

  const selectTag = (tag: AvailableTag) => {
    if (selectedTagIds.includes(tag.id)) return;
    onChangeTagIds([...selectedTagIds, tag.id]);
  };

  return (
    <Skeleton isLoaded={!isFetchingTags} className="min-h-7 rounded-md">
      <LimeAlert message={processedErrorMessageTags} />
      <div className="flex flex-wrap items-center gap-1">
        {appliedDefaults.map((def) => (
          <Chip
            key={`default-${def.id}`}
            variant="flat"
            size="sm"
            radius="sm"
            onClose={
              isDisabled
                ? undefined
                : () =>
                    onChangeDefaultIds(
                      selectedDefaultIds.filter((id) => id !== def.id),
                    )
            }
          >
            {def.name}
          </Chip>
        ))}
        {appliedTags.map((tag) => (
          <Chip
            key={`tag-${tag.id}`}
            variant="flat"
            size="sm"
            radius="sm"
            onClose={
              isDisabled
                ? undefined
                : () =>
                    onChangeTagIds(selectedTagIds.filter((id) => id !== tag.id))
            }
          >
            {tag.name}
          </Chip>
        ))}

        <Popover
          placement="bottom-start"
          isOpen={isPopoverOpen}
          onOpenChange={(open) => {
            if (isDisabled) return;
            setIsPopoverOpen(open);
          }}
        >
          <PopoverTrigger>
            <Chip
              as="button"
              type="button"
              variant="bordered"
              size="sm"
              radius="sm"
              startContent={<Tag size={12} />}
              isDisabled={isDisabled}
              className="cursor-pointer"
            >
              <Trans>Dodaj oznako zapiska</Trans>
            </Chip>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-3">
            <div className="flex w-full flex-col gap-2">
              {(suggestedDefaults.length > 0 || suggestedTags.length > 0) && (
                <div className="flex flex-wrap gap-1">
                  {suggestedDefaults.map((def) => (
                    <Chip
                      key={`default-${def.id}`}
                      as="button"
                      type="button"
                      size="sm"
                      radius="sm"
                      variant="bordered"
                      onClick={() => selectDefault(def)}
                      className="cursor-pointer"
                    >
                      {def.name}
                    </Chip>
                  ))}
                  {suggestedTags.map((tag) => (
                    <Chip
                      key={`tag-${tag.id}`}
                      as="button"
                      type="button"
                      size="sm"
                      radius="sm"
                      variant="bordered"
                      onClick={() => selectTag(tag)}
                      className="cursor-pointer"
                    >
                      {tag.name}
                    </Chip>
                  ))}
                </div>
              )}

              <Button
                size="sm"
                variant="light"
                startContent={<Plus size={14} />}
                onPress={() => {
                  setIsPopoverOpen(false);
                  openCreateTagModal();
                }}
              >
                <Trans>Ustvari novo oznako</Trans>
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <ManageCustomerNoteTagModal
        isOpen={isCreateTagModalOpen}
        onClose={({ createdTagId }) => {
          closeCreateTagModal();
          if (createdTagId) {
            onChangeTagIds([...selectedTagIds, createdTagId]);
          }
        }}
      />
    </Skeleton>
  );
};
